import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clearStoredAuthToken, setStoredAuthToken, getStoredAuthToken, getStoredRefreshToken } from '../utils/authStorage';
import { subscribeToAuthEvents, emitAuthEvent, AUTH_EVENT_TYPES } from '../utils/authEvents';
import { getCurrentUser } from '../services/usersApi';
import { initProactiveRefresh, cleanupProactiveRefresh, scheduleProactiveRefresh } from '../utils/tokenRefreshManager';
import { attemptCrossDomainTransfer } from '../utils/crossDomainAuth';
import { performRefresh } from '../services/apiClient';
import { identifyVisitor } from '../umami';

const SESSION_STORAGE_KEY = 'novelday:viewer-session-id';
const USER_STORAGE_KEY = 'novelday:user-id';
const USER_PROFILE_KEY = 'novelday:user-profile';

const generateSessionId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `session-${Math.random().toString(36).slice(2)}-${Date.now()}`;
};

const ensureSessionId = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const existing = window.localStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) {
    return existing;
  }
  const sessionId = generateSessionId();
  window.localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  return sessionId;
};

const readUserId = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const stored = window.localStorage.getItem(USER_STORAGE_KEY);
  if (!stored) {
    return null;
  }
  const parsed = Number(stored);
  return Number.isFinite(parsed) ? parsed : null;
};

const readUserProfile = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  const stored = window.localStorage.getItem(USER_PROFILE_KEY);
  if (!stored) {
    return null;
  }
  try {
    return JSON.parse(stored);
  } catch (_error) {
    return null;
  }
};

const ViewerIdentityContext = createContext(null);

export const ViewerIdentityProvider = ({ children }) => {
  const [userId, setUserIdState] = useState(() => readUserId());
  const [user, setUserState] = useState(() => readUserProfile());
  const sessionId = useMemo(() => ensureSessionId(), []);

  const setUserId = useCallback((nextUserId) => {
    if (typeof window === 'undefined') {
      setUserIdState(nextUserId ?? null);
      return;
    }

    if (nextUserId === null || nextUserId === undefined) {
      window.localStorage.removeItem(USER_STORAGE_KEY);
      setUserIdState(null);
      return;
    }

    const numericId = Number(nextUserId);
    if (!Number.isFinite(numericId)) {
      throw new Error('User id must be a finite number.');
    }
    window.localStorage.setItem(USER_STORAGE_KEY, String(numericId));
    setUserIdState(numericId);
  }, []);

  const setUser = useCallback((nextUser) => {
    if (typeof window === 'undefined') {
      if (!nextUser) {
        setUserState(null);
        setUserId(null);
        return;
      }
      const { token: _token, ...rest } = nextUser;
      setUserState(rest);
      setUserId(rest.id ?? null);
      return;
    }

    if (!nextUser) {
      window.localStorage.removeItem(USER_PROFILE_KEY);
      setUserState(null);
      setUserId(null);
      clearStoredAuthToken();
      emitAuthEvent({ type: AUTH_EVENT_TYPES.PROFILE_UPDATED, payload: null });
      return;
    }

    const { token, ...rest } = nextUser;
    window.localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(rest));
    setUserState(rest);
    setUserId(rest.id ?? null);
    if (token) {
      setStoredAuthToken(token);
    }
    emitAuthEvent({ type: AUTH_EVENT_TYPES.PROFILE_UPDATED, payload: rest });
  }, [setUserId]);

  const signOut = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(USER_PROFILE_KEY);
      window.localStorage.removeItem(USER_STORAGE_KEY);
    }
    setUserState(null);
    setUserIdState(null);
    clearStoredAuthToken();
    emitAuthEvent({ type: AUTH_EVENT_TYPES.PROFILE_UPDATED, payload: null });
  }, []);

  const refreshFromBackend = useCallback(async () => {
    let token = getStoredAuthToken();
    // If the JWT is absent but a refresh token exists, attempt to refresh
    // before giving up. This handles the case where a transient refresh
    // failure on a previous page load cleared the JWT via clearAuthTokenOnly()
    // but left the refresh token intact — without this, the app gets stuck
    // in a logged-out state because neither refreshFromBackend nor the
    // proactive refresh manager will attempt a refresh without a JWT.
    if (!token) {
      const refreshToken = getStoredRefreshToken();
      if (!refreshToken) return;
      try {
        token = await performRefresh(refreshToken);
      } catch (_error) {
        return;
      }
    }
    try {
      const freshUser = await getCurrentUser();
      if (freshUser) {
        setUserState(freshUser);
        setUserId(freshUser.id ?? null);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(freshUser));
        }
      }
    } catch (_error) {
      // If the token is invalid, the auth interceptor will handle it.
    }
  }, [setUserId]);

  useEffect(() => {
    const unsubscribe = subscribeToAuthEvents((event) => {
      if (event?.type === AUTH_EVENT_TYPES.UNAUTHORIZED) {
        setUser(null);
      } else if (event?.type === AUTH_EVENT_TYPES.PROFILE_UPDATED) {
        const nextUser = event.payload;
        setUserState(nextUser);
        setUserIdState(nextUser?.id ?? null);
      }
    });
    return unsubscribe;
  }, [setUser]);

  // Refresh profile from backend on mount — single call for the entire app.
  // Ref guard prevents double-fetch in React StrictMode (dev mode double-mounts effects).
  // Also attempts cross-domain token transfer from novelday.cc for users who
  // logged in before the 308 redirect was added.
  const hasInitialFetchRef = useRef(false);
  useEffect(() => {
    if (hasInitialFetchRef.current) return;
    hasInitialFetchRef.current = true;
    (async () => {
      // If no JWT on .net, try to recover. Two paths:
      // 1. If we have a refresh token, refreshFromBackend will use it to
      //    get a new JWT — no cross-domain transfer needed.
      // 2. If no refresh token either, try transferring from .cc (handles
      //    users who logged in before the 308 redirect was added).
      if (!getStoredAuthToken() && !getStoredRefreshToken()) {
        const transferred = await attemptCrossDomainTransfer();
        if (transferred) {
          scheduleProactiveRefresh();
        }
      }
      refreshFromBackend();
    })();
  }, [refreshFromBackend]);

  // Proactive JWT refresh — refreshes token at 80% of its lifetime, before it
  // expires. Also handles tab visibility changes (refreshes when tab becomes
  // visible if token is near expiry). Eliminates the reactive 401→refresh cycle.
  useEffect(() => {
    initProactiveRefresh();
    return () => cleanupProactiveRefresh();
  }, []);

  // Reschedule proactive refresh whenever the user logs in (userId changes from null to a value)
  useEffect(() => {
    if (userId) {
      scheduleProactiveRefresh();
    }
  }, [userId]);

  useEffect(() => {
    identifyVisitor({ account: userId ? 'member' : 'guest' });
  }, [userId]);

  const value = useMemo(() => ({
    userId,
    user,
    sessionId,
    setUserId,
    setUser,
    signOut,
    refreshFromBackend,
  }), [userId, user, sessionId, setUserId, setUser, signOut, refreshFromBackend]);

  return (
    <ViewerIdentityContext.Provider value={value}>
      {children}
    </ViewerIdentityContext.Provider>
  );
};

export default ViewerIdentityContext;
